import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { formatCategory, getCategoryColor } from '@/lib/constants/integration-categories';
import type { TenantIntegrationInstance, IntegrationDefinition } from '@/hooks/use-integrations';
import type { TenantRead } from '@harmonyso/tenants-sdk';

interface IntegrationsGridProps {
  instances: TenantIntegrationInstance[];
  tenants?: TenantRead[];
  definitions?: IntegrationDefinition[];
}

export function IntegrationsGrid({ instances, tenants, definitions }: IntegrationsGridProps) {
  const tenantMap = useMemo(() => {
    if (!tenants) return new Map<string, string>();
    return new Map(tenants.map(t => [t.id, t.company || t.id.slice(0, 8)]));
  }, [tenants]);

  const definitionMap = useMemo(() => {
    if (!definitions) return new Map<string, IntegrationDefinition>();
    return new Map(definitions.map(d => [d.id, d]));
  }, [definitions]);

  if (instances.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        No integration instances match the current filters.
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {instances.map(instance => {
        const def = definitionMap.get(instance.integration_id);
        const tenantName = tenantMap.get(instance.tenant_id) ?? instance.tenant_id.slice(0, 8);
        const category = def?.category_id ?? '';
        const colorClass = getCategoryColor(category);
        const instanceLabel = getInstanceLabel(instance, def);

        return (
          <div
            key={instance.id}
            className={`rounded-lg border p-4 transition-colors hover:shadow-sm ${colorClass}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-semibold truncate">
                  {def?.name ?? instance.integration_id}
                </h4>
                <Link
                  to="/tenants/$tenantId"
                  params={{ tenantId: instance.tenant_id }}
                  className="text-xs text-muted-foreground hover:text-primary hover:underline truncate block mt-0.5"
                >
                  {tenantName}
                </Link>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                {category && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    {formatCategory(category)}
                  </Badge>
                )}
                {instance.deprecated ? (
                  <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Deprecated</Badge>
                ) : (
                  <Badge variant="default" className="text-[10px] px-1.5 py-0">Active</Badge>
                )}
              </div>
            </div>
            {instanceLabel && (
              <p className="text-xs text-muted-foreground mt-1.5 truncate">{instanceLabel}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function getInstanceLabel(instance: TenantIntegrationInstance, def?: IntegrationDefinition): string | null {
  const displayName = instance.label || instance.name;
  if (!displayName) return null;
  if (displayName === def?.name) return null;
  if (displayName === instance.integration_id) return null;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}/.test(displayName)) return null;
  if (/^T[A-Z0-9]{9,}$/.test(displayName)) return null;
  return displayName;
}
